import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LogIn, Mail, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loginWithEmail, loginWithGoogle, isAdmin } from "../firebase";
import useAuth from "../useAuth";

export default function AdminLogin() {
  const { user, loading, admin } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <p className="text-muted text-sm">Loading…</p>
      </div>
    );
  }

  // Logged in — but not an admin: tell them and provide sign-out.
  if (user && !admin) {
    return (
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <ShieldCheck className="h-10 w-10 text-coral mx-auto" />
        <h1 className="font-display text-display-md mt-6 text-ink tracking-tightest">
          Not authorised.
        </h1>
        <p className="mt-4 text-ink/65">
          The account <span className="font-medium text-ink">{user.email}</span> isn't on the admin
          allowlist. Ask the project owner to add you.
        </p>
      </div>
    );
  }

  if (admin) {
    const from = location.state?.from || "/admin";
    return <Navigate to={from} replace />;
  }

  const handleEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cred = await loginWithEmail(email, password);
      if (!isAdmin(cred.user)) {
        toast.error("Not authorised", { description: "This email isn't on the admin allowlist." });
      }
    } catch (err) {
      toast.error("Login failed", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    try {
      const cred = await loginWithGoogle();
      if (!isAdmin(cred.user)) {
        toast.error("Not authorised", { description: "This Google account isn't on the admin allowlist." });
      }
    } catch (err) {
      toast.error("Google sign-in failed", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="max-w-md mx-auto px-6 py-20 md:py-24">
      <p className="section-num">
        <strong>·</strong> Admin
      </p>
      <h1 className="font-display text-display-md mt-5 text-ink tracking-tightest">
        Sign in to the
        <br />
        <span className="font-italic text-coral">organo desk.</span>
      </h1>
      <p className="mt-4 text-ink/65 text-[15px]">
        Email / password or Google. Only allowlisted accounts can see orders,
        messages and subscribers.
      </p>

      <form onSubmit={handleEmail} data-testid="admin-email-login" className="mt-10 bg-boneSoft border border-ink/10 rounded-4xl p-7 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-muted block mb-2">Email</label>
          <div className="flex items-center gap-3 bg-bone border border-ink/15 rounded-full px-5 py-3">
            <Mail className="h-4 w-4 text-muted" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="admin-email"
              className="flex-1 bg-transparent outline-none text-ink placeholder:text-muted text-sm"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-muted block mb-2">Password</label>
          <div className="flex items-center gap-3 bg-bone border border-ink/15 rounded-full px-5 py-3">
            <Lock className="h-4 w-4 text-muted" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-password"
              className="flex-1 bg-transparent outline-none text-ink placeholder:text-muted text-sm"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-submit"
          className="btn-primary w-full justify-center disabled:opacity-60"
        >
          <LogIn className="h-4 w-4" /> Sign in
          <span className="knob">→</span>
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-muted">
        <span className="flex-1 h-px bg-ink/10" />
        <span>or</span>
        <span className="flex-1 h-px bg-ink/10" />
      </div>

      <button
        onClick={handleGoogle}
        disabled={busy}
        data-testid="admin-google"
        className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-bone border border-ink/15 hover:border-ink text-ink py-3 text-sm font-medium transition-colors disabled:opacity-60"
      >
        <svg className="h-4 w-4" viewBox="0 0 48 48" fill="none">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.6 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.2-2.4-.4-3.5z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5.1l-6.2-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.5-3.3-11.2-8l-6.5 5C9.5 39.6 16.1 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.5 5.9l6.2 5.3C40 36 44 30.5 44 24c0-1.2-.2-2.4-.4-3.5z" />
        </svg>
        Continue with Google
      </button>
    </div>
  );
}
